import React, { memo } from "react";

import { classNames } from "helpers";
import { ShowcaseBackdrop, ShowcaseCard, ShowcaseStage } from "ui/components/Showcase";
import Logo from "ui/modules/public/components/Logo/Logo";

import { VEHICLE_WIZARD_INTRO_TEXTS } from "./consts";
import DocumentsScene from "./scenes/DocumentsScene";
import EquipmentScene from "./scenes/EquipmentScene";
import ExtinguisherScene from "./scenes/ExtinguisherScene";
import InfoScene from "./scenes/InfoScene";
import LocationScene from "./scenes/LocationScene";
import OwnerScene from "./scenes/OwnerScene";
import PickerScene from "./scenes/PickerScene";
import PicturesScene from "./scenes/PicturesScene";
import PriceScene from "./scenes/PriceScene";
import TiresScene from "./scenes/TiresScene";
import VehicleRow from "./scenes/VehicleRow";
import { VehicleWizardIntroProps, VehicleWizardSceneProps } from "./types";

/** Scena za svaki korak čarobnjaka, po ključu koraka. */
const STEP_SCENES: Record<string, React.FC<VehicleWizardSceneProps>> = {
  picker: PickerScene,
  info: InfoScene,
  tires: TiresScene,
  location: LocationScene,
  equipment: EquipmentScene,
  extinguisher: ExtinguisherScene,
  owner: OwnerScene,
  price: PriceScene,
  pictures: PicturesScene,
  documents: DocumentsScene,
};

const DEFAULT_STEP_KEY = "picker";

/**
 * Lijevi panel čarobnjaka vozila: za svaki korak vlastita scena s karticama koje prikazuju
 * upisane podatke, a ispod objašnjenje koraka. Ide po ključu koraka, jer su koraci po
 * tvrtki uključivi pa se redni broj ne poklapa uvijek s istim korakom.
 */
const VehicleWizardIntro: React.FC<VehicleWizardIntroProps> = ({ vehicleInfo, stepKey, stepKeys }) => {
  const currentKey = stepKey && STEP_SCENES[stepKey] ? stepKey : DEFAULT_STEP_KEY;
  const Scene = STEP_SCENES[currentKey];
  const text = VEHICLE_WIZARD_INTRO_TEXTS[currentKey];
  const stepIndex = stepKeys.indexOf(currentKey);

  return (
    <div className="relative flex flex-col w-full h-full bg-blue-700 overflow-hidden">
      <ShowcaseBackdrop />
      <Logo variant="light" animated className="absolute top-8 left-8 z-10" />

      <div className="relative flex flex-1 min-h-0 items-center justify-center select-none pointer-events-none" aria-hidden="true">
        {/* `key`: novi korak iznova montira scenu, pa kartice ponovno ulete. */}
        <ShowcaseStage key={currentKey}>
          <Scene vehicleInfo={vehicleInfo} />
          {/* Vozilo koje se unosi, na svakom koraku na istom mjestu. Na odabiru je ono glavna kartica. */}
          {currentKey !== DEFAULT_STEP_KEY && (
            <ShowcaseCard className="left-[10.5rem] top-[-3.5rem] w-[14rem]" delay={0.6}>
              <VehicleRow vehicleInfo={vehicleInfo} />
            </ShowcaseCard>
          )}
        </ShowcaseStage>
      </div>

      <div className="relative px-8 pb-12 text-center">
        {stepIndex >= 0 && (
          <div className="text-xs font-semibold uppercase tracking-widest text-blue-200">
            Korak {stepIndex + 1} od {stepKeys.length}
          </div>
        )}
        <div key={currentKey} className="motion-safe:animate-showcaseFadeIn">
          <div className="mt-2 text-3xl font-bold leading-tight text-white">{text.title}</div>
          {/* Visina dva retka: opisi su različite duljine, a scena iznad ne smije skakati. */}
          <p className="mt-3 mx-auto max-w-md min-h-[3rem] text-base text-blue-100">{text.description}</p>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          {stepKeys.map((key) => (
            <span
              key={key}
              className={classNames(
                "h-1.5 rounded-full transition-all duration-300",
                key === currentKey ? "w-6 bg-amber-400" : "w-1.5 bg-white/40"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default memo(VehicleWizardIntro);
